import { useCallback, useState } from 'react';
import {
  Panel, ReactFlowProvider,
} from '@xyflow/react';
import { clsx } from 'clsx';

import CompanyMapModals from '../../../components/CompanyMapModals/CompanyMapModals';
import RenameModal from '../../../components/RenameModal/RenameModal';
import { VALUE_CHAIN_AI_AGENT } from '../../../constants/general';
import { COMPANY_MAP_MODALS, COMPANY_MAP_TABS } from '../../../constants/interfaces';
import BottomRightControls from './components/BottomRightControls';
import BusinessStructure from './components/BusinessStructure/BusinessStructure';
import ReviewGeneratedPanel from './components/ReviewGeneratedPanel';
import TopCenterControls from './components/TopCenterControls';
import TopLeftControls from './components/TopLeftControls';
import TopRightControls from './components/TopRightControls';
import { DndProvider } from './components/ValueChain/components/DndContext';
import ValueChain from './components/ValueChain/ValueChain';

import type { ICompanyMap } from './interfaces/ICompanyMap';

import styles from './sass/CompanyMapView.module.scss';

function CompanyMapView({
  currentTab, onTabHandler, onRenameHandler, activeModal, closeModal, onRenameSubmit, valueChainMapId,
  isProfitCentreModalOpen, closeProfitCenterModal, openProfitCenterModal, currentZoom,
  onMoveEnd, data, isLoading, onChangeStatus, isFullScreenActive, onMaximizeHandler, isAccessDisabled,
  selectedProfitCennter, setSelectedProfitCenter, setCurrentTargetSwimlaneId, currentTargetSwimlaneId,
  openCustomerModal, setSelectedCustomer, isCustomerModalOpen, selectedCustomer,
  openSupplierModal, setSelectedSupplier, isSupplierModalOpen, selectedSupplier,
  openBalanceSheetAssetsModal, setSelectedBalanceSheetAssets, isBalanceSheetAssetsModalOpen, selectedBalanceSheetAssets,
  openInputProductModal, setSelectedInputProduct, isInputProductModalOpen, selectedInputProduct,
  launchpadDiscoverEntry,
}: ICompanyMap) {
  const [onReviewGeneratedBack, setOnReviewGeneratedBack] = useState<(() => void) | undefined>(undefined);
  const [isReviewGeneratedVisible, setIsReviewGeneratedVisible] = useState(false);
  const [reviewGeneratedSessionSubType, setReviewGeneratedSessionSubType] = useState<string | undefined>(undefined);

  const handleRegisterReviewGeneratedBackHandler = useCallback((handler: () => void) => {
    setOnReviewGeneratedBack(() => handler);
  }, []);
  const handleReviewGeneratedVisibilityChange = useCallback((isVisible: boolean, sessionSubType?: string) => {
    setIsReviewGeneratedVisible(isVisible);
    setReviewGeneratedSessionSubType(sessionSubType);
  }, []);
  const isReviewGeneratedHeaderVisible = currentTab === COMPANY_MAP_TABS.VALUE_CHAIN_MAP
    && isReviewGeneratedVisible
    && (
      reviewGeneratedSessionSubType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE
      || reviewGeneratedSessionSubType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
    );

  return (
    <div className={styles.container}>
      <ReactFlowProvider>
        <BottomRightControls
          currentZoom={currentZoom}
          onMaximizeHandler={onMaximizeHandler}
          isFullScreenActive={isFullScreenActive}
        />
        {isReviewGeneratedHeaderVisible ? (
          <Panel
            position="top-left"
            className={clsx(styles.topControls, {
              [styles.fullScreen]: isFullScreenActive,
              [styles.reviewPanel]: isReviewGeneratedHeaderVisible,
            })}
          >
            <ReviewGeneratedPanel
              sessionSubType={reviewGeneratedSessionSubType}
              onBack={onReviewGeneratedBack}
            />
          </Panel>
        ) : (
          <>
            <Panel
              position="top-left"
              className={clsx(styles.topControls, styles.leftPanel, {
                [styles.fullScreen]: isFullScreenActive,
              })}
            >
              <TopLeftControls
                onRenameHandler={onRenameHandler}
                data={data}
                isLoading={isLoading}
                onChangeStatus={onChangeStatus}
                isFullScreenActive={isFullScreenActive}
                isAccessDisabled={isAccessDisabled}
              />
            </Panel>
            <Panel
              position="top-center"
              className={clsx(styles.topControls, {
                [styles.fullScreen]: isFullScreenActive,
              })}
            >
              <TopCenterControls
                onTabHandler={onTabHandler}
                currentTab={currentTab}
              />
            </Panel>
            {!isAccessDisabled && (
              <Panel
                position="top-right"
                className={clsx(styles.topControls, {
                  [styles.fullScreen]: isFullScreenActive,
                })}
              >
                <TopRightControls />
              </Panel>
            )}
          </>
        )}
        {currentTab === COMPANY_MAP_TABS.BUSINESS_STRUCTURE ? (
          <BusinessStructure
            valueChainMapId={valueChainMapId}
            onMoveEnd={onMoveEnd}
            openProfitCenterModal={openProfitCenterModal}
            isFullScreenActive={isFullScreenActive}
            isAccessDisabled={isAccessDisabled}
            setSelectedProfitCenter={setSelectedProfitCenter}
          />
        ) : (
          <DndProvider>
            <ValueChain
              onMoveEnd={!isAccessDisabled ? onMoveEnd : () => {}}
              valueChainMapId={valueChainMapId}
              isFullScreenActive={isFullScreenActive}
              isAccessDisabled={isAccessDisabled}
              openProfitCenterModal={openProfitCenterModal}
              setSelectedProfitCenter={setSelectedProfitCenter}
              setCurrentTargetSwimlaneId={setCurrentTargetSwimlaneId}
              currentTargetSwimlaneId={currentTargetSwimlaneId}
              isProfitCentreModalOpen={isProfitCentreModalOpen}
              openCustomerModal={openCustomerModal}
              setSelectedCustomer={setSelectedCustomer}
              openSupplierModal={openSupplierModal}
              setSelectedSupplier={setSelectedSupplier}
              openBalanceSheetAssetsModal={openBalanceSheetAssetsModal}
              setSelectedBalanceSheetAssets={setSelectedBalanceSheetAssets}
              openInputProductModal={openInputProductModal}
              setSelectedInputProduct={setSelectedInputProduct}
              onRegisterReviewGeneratedBackHandler={handleRegisterReviewGeneratedBackHandler}
              onReviewGeneratedVisibilityChange={handleReviewGeneratedVisibilityChange}
              launchpadDiscoverEntry={launchpadDiscoverEntry}
            />
          </DndProvider>
        )}
      </ReactFlowProvider>
      {data ? (
        <RenameModal
          open={activeModal === COMPANY_MAP_MODALS.RENAME}
          onSubmit={onRenameSubmit}
          onReject={closeModal}
          name={data.name}
        />
      ) : null}
      <CompanyMapModals
        valueChainMapId={valueChainMapId}
        currentTargetSwimlaneId={currentTargetSwimlaneId}
        isProfitCentreModalOpen={isProfitCentreModalOpen}
        closeProfitCenterModal={closeProfitCenterModal}
        selectedProfitCennter={selectedProfitCennter}
        setSelectedProfitCenter={setSelectedProfitCenter}
        isCustomerModalOpen={isCustomerModalOpen}
        setSelectedCustomer={setSelectedCustomer}
        selectedCustomer={selectedCustomer}
        isSupplierModalOpen={isSupplierModalOpen}
        setSelectedSupplier={setSelectedSupplier}
        selectedSupplier={selectedSupplier}
        isInputProductModalOpen={isInputProductModalOpen}
        setSelectedInputProduct={setSelectedInputProduct}
        selectedInputProduct={selectedInputProduct}
        isBalanceSheetAssetsModalOpen={isBalanceSheetAssetsModalOpen}
        setSelectedBalanceSheetAssets={setSelectedBalanceSheetAssets}
        selectedBalanceSheetAssets={selectedBalanceSheetAssets}
      />
    </div>
  );
}

export default CompanyMapView;
